import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { FC, useCallback, useDeferredValue, useMemo, useState } from 'react';
import { components, ControlProps } from 'react-select';
import TimeIcon from '~/assets/svgs/close.svg';
import DocumentFilterIcon from '~/assets/svgs/document-filter.svg';
import FilterIcon from '~/assets/svgs/filter.svg';
import Breadcrumb, { BreadcrumbItem } from '~/components/Breadcrumb';
import Button from '~/components/Button';
import Container from '~/components/Container';
import Divider from '~/components/Divider';
import ProductItem from '~/components/Items/ProductItem';
import Select from '~/components/Select';
import Text from '~/components/Text';
import { ShortCodeFilterKey } from '~/constants/enum';
import useClient from '~/hooks/useClient';
import { Product } from '~/services/product';
import { converShortCodeFilterKey } from '~/utils/converters';
import { generateSortList } from '~/utils/filter';
import { formatNumber } from '~/utils/number';

const DynamicDrawer = dynamic(() => import('~/components/Drawer'), {
    ssr: false
});
const DynamicPriceFilter = dynamic(() => import('~/components/Filters/PriceFilter'));
const DynamicCheckboxFilter = dynamic(() => import('~/components/Filters/CheckboxFilter'));
const DynamicListContainer = dynamic(() => import('~/components/ListContainer'));

export interface ProductListProps {
    breadcrumbs?: BreadcrumbItem[];
    isLoading?: boolean;
    data?: Product[];
    filters?: {
        aggregations?: FilterType['aggregations'];
        sortFields?: FilterType['sort_fields'];
    };
    defaultSort?: string;
    title?: string;
    total?: number;
    isShowFilter?: boolean;
    identifier: 'collection' | 'category' | 'search';
    listKey: any;
    showResult?: boolean;
}

const ProductList: FC<ProductListProps> = ({
    breadcrumbs,
    isLoading,
    data,
    filters,
    title,
    total,
    isShowFilter,
    defaultSort = 'relevance',
    listKey,
    identifier,
    showResult = false
}) => {
    const isClient = useClient();
    const { query, push, pathname } = useRouter();
    const queryFilter = converShortCodeFilterKey(
        {
            currentPage: '1',
            pageSize: '35',
            // sort: "best_sales-DESC",
            ...query
        },
        'decode'
    );

    const aggregations = filters?.aggregations || [];
    const sortFields = filters?.sortFields;
    const resetFilterKey = query?.keyword as string;

    const [openFilter, setOpenFilter] = useState(false);

    const totalPageDeferred = useDeferredValue(total);

    const onChangeFilter = (property: string, value: any) => {
        if (property === 'sort') {
            return push(
                {
                    pathname: pathname,
                    query: {
                        ...query,
                        sort: value,
                        currentPage: 1
                    }
                },
                undefined,
                { shallow: true }
            );
        }

        if (property === 'price') {
            const priceQuery = {
                ...query,
                min_price: value?.from,
                max_price: value?.to,
                currentPage: 1
            };

            if (Object.values(value || {}).some((v) => v === undefined)) {
                delete priceQuery.min_price;
                delete priceQuery.max_price;
            }

            return push(
                {
                    pathname: pathname,
                    query: priceQuery
                },
                undefined,
                { shallow: true }
            );
        }

        const convertProperty = ShortCodeFilterKey[property as keyof typeof ShortCodeFilterKey] || property;

        push(
            {
                pathname: pathname,
                query: {
                    ...query,
                    [convertProperty]: !!value?.length
                        ? value?.filter((v: string) => Boolean(v))?.join(',')
                        : undefined,
                    currentPage: 1
                }
            },
            undefined,
            { shallow: true }
        );
    };

    const renderFilter = useCallback(
        (filterItem: Aggregation) => {
            // if (displayFilter.indexOf(filterItem.attribute_code) === -1) return null;
            // if(filterItem.attribute_code === "price") return null
            return (
                <DynamicCheckboxFilter
                    title={filterItem.label}
                    options={filterItem.options.map((option: OptionAggregation) => {
                        return {
                            label: `${option.label} ${option.count ? `(${option.count})` : ''}`,
                            value: option.value
                        };
                    })}
                    value={queryFilter[filterItem.attribute_code]?.split(',') || []}
                    onChange={(v: string[]) => onChangeFilter(filterItem.attribute_code, v)}
                />
            );
            // switch (filterItem.attribute_code) {
            //   // case "price": {
            //   //   return <PriceFilter
            //   //     onChange={(value) => onChangeFilter("price", value)}
            //   //     values={{
            //   //       from: queryFilter.min_price ? parseInt(queryFilter.min_price) : 0,
            //   //       to: queryFilter.max_price ? parseInt(queryFilter.max_price) : 1000,
            //   //     }}
            //   //     loading={isLoading}
            //   //   />;
            //   // }

            //   // case "color": {
            //   //   return (
            //   //     <FilterItem title="Color">
            //   //       <ColorSelect
            //   //         options={[
            //   //           { label: "black", value_index: 0 },
            //   //           { label: "green", value_index: 1 },
            //   //           { label: "white", value_index: 2 },
            //   //           { label: "yellow", value_index: 3 },
            //   //           { label: "orange", value_index: 4 },
            //   //         ]}
            //   //         value={queryFilter.color}
            //   //         onChange={(v: {
            //   //           label: string;
            //   //           value_index: number;
            //   //           disabled?: boolean;
            //   //         }) => onChangeFilter("color", v.value_index)}
            //   //       />
            //   //     </FilterItem>
            //   //   );
            //   // }
            //   default: {
            //     return (
            //       <DynamicCheckboxFilter
            //         title={filterItem.label}
            //         options={filterItem.options.map((option: OptionAggregation) => {
            //           return {
            //             label: `${option.label} ${
            //               option.count ? `(${option.count})` : ""
            //             }`,
            //             value: option.value,
            //           };
            //         })}
            //         value={queryFilter[filterItem.attribute_code]?.split(",") || []}
            //         onChange={(v: string[]) =>
            //           onChangeFilter(filterItem.attribute_code, v)
            //         }
            //       />
            //     );
            //   }
            // }
        },
        [queryFilter, onChangeFilter, isLoading]
    );
    const { pageSize = 35, currentPage = 1 } = queryFilter;

    const sortOptions = useMemo(() => {
        if (isEmpty(sortFields?.options)) return [];

        return generateSortList(sortFields?.options || [])?.map((s: SortFieldsConvert) => {
            return {
                label: s.title,
                value: s.combineValue
            };
        });
    }, [sortFields]);

    const priceAggregations = useMemo(() => {
        return aggregations?.find((aggregation) => aggregation.attribute_code === 'price');
    }, [aggregations]);

    const regex = /\b(\d{12})(-p)?\b/;
    const matching: any = (query?.ref as string)?.match(regex);
    const isRefSku = !!matching?.[1];

    return (
        <Container className="py-5 flex flex-col gap-3">
            <div className="hidden lg:block">{!!breadcrumbs && <Breadcrumb items={breadcrumbs} />}</div>
            <div className="flex flex-col lg:flex-row gap-3">
                <div className="flex-1">
                    <div className="flex flex-col gap-3">
                        {isRefSku && (
                            <ProductItem
                                data={
                                    {
                                        sku: query?.ref
                                    } as Product
                                }
                                type="horizontal"
                                imageProps={{ className: '!max-w-[350px]' }}
                                nameProps={{ lineClamp: 1 }}
                            />
                        )}
                        {
                            !isClient && (title ? (
                                <Text type="headline-1" weight={800}>
                                    {title}
                                </Text>
                            ) : (
                                <Divider />
                            ))
                        }
                        {isClient && (!isRefSku && title ? (
                            <Text type="headline-1" weight={800}>
                                {title}
                            </Text>
                        ) : (
                            <Divider />
                        ))}

                        <DynamicListContainer
                            search={query?.keyword as string}
                            listKey={listKey}
                            identifier={identifier}
                            loading={isLoading}
                            products={data}
                            showListLayout={false}
                            defaultLayout="grid-5"
                            extra={
                                <div className="flex items-center justify-between gap-3 w-full">
                                    <div className="block lg:hidden">
                                        <Button
                                            shape="square"
                                            type="borderless"
                                            onClick={() => setOpenFilter(true)}
                                            prefixIcon={
                                                <DocumentFilterIcon
                                                    className="text-black"
                                                    width={32}
                                                    height={32}
                                                    alt="filter"
                                                />
                                            }
                                        />
                                    </div>
                                    <div className="hidden lg:block">
                                        <Button
                                            variant="secondary"
                                            onClick={() => setOpenFilter(true)}
                                            prefixIcon={<FilterIcon width={16} height={16} alt="filter" />}
                                        >
                                            Filter
                                        </Button>
                                    </div>
                                    {!Number.isNaN(Number(query?.min_price)) &&
                                        !Number.isNaN(Number(query?.max_price)) && (
                                            <div>
                                                <div
                                                    className="inline-flex bg-gray-200 text-gray-700 rounded-full lg:px-4 px-2  py-1 font-semibold 
                  hover:bg-gray-300 lg:text-lg text-sm items-center justify-between"
                                                >
                                                    {Number(query?.min_price) === 0 &&
                                                        Number(query?.max_price) > 0 &&
                                                        `under $${query?.max_price}`}
                                                    {Number(query?.min_price) > 0 && Number(query?.max_price) > 0
                                                        ? `$${query?.min_price} - $${query?.max_price}`
                                                        : Number(query?.min_price) === 100 &&
                                                            Number(query?.max_price) === 200
                                                            ? `Over $${query?.min_price}`
                                                            : null}

                                                    <span
                                                        onClick={() =>
                                                            onChangeFilter('price', {
                                                                from: undefined,
                                                                to: undefined
                                                            })
                                                        }
                                                        className=" hover:cursor-pointer"
                                                    >
                                                        <TimeIcon width={24} height={24} />
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                    {!!sortOptions.length && (
                                        <div className="flex items-center flex-1 gap-5 justify-end">
                                            {!!total && total > 0 ? (
                                                <span className="text-sm">
                                                    {total > 1000 ? `${formatNumber(1000)}+` : formatNumber(total)}{' '}
                                                    results
                                                </span>
                                            ) : total === 0 ? (
                                                <span className="text-sm">no items</span>
                                            ) : null}
                                            <Select
                                                className="flex-1 max-w-[300px]"
                                                options={[
                                                    {
                                                        label: 'Relevance',
                                                        value: 'relevance-DESC'
                                                    }
                                                ].concat(sortOptions)}
                                                onChange={(value: string) => onChangeFilter('sort', value)}
                                                value={queryFilter.sort || defaultSort}
                                                isSearchable={false}
                                                components={{ Control }}
                                            />
                                        </div>
                                    )}
                                </div>
                            }
                            pageSize={Number(pageSize)}
                            total={totalPageDeferred}
                            currentPage={currentPage}
                        />
                    </div>
                </div>
            </div>
            <DynamicDrawer
                title={
                    <div className="flex flex-col flex-1 gap-1">
                        <Text type="title-3">Filter</Text>
                    </div>
                }
                titleClassName="px-5 pt-5"
                direction="left"
                open={openFilter}
                onClose={() => setOpenFilter(false)}
                className="flex flex-col w-80 "
            >
                <div className="my-4">
                    <Divider />
                </div>
                <div className="flex flex-col gap-4 h-full flex-1 overflow-y-auto overflow-x-visible px-5">
                    <DynamicPriceFilter
                        onChange={(value) => onChangeFilter('price', value)}
                        values={{
                            from: queryFilter.min_price ? Number(queryFilter.min_price) : undefined,
                            to: queryFilter.max_price ? Number(queryFilter.max_price) : undefined
                        }}
                        aggregations={priceAggregations}
                        key={resetFilterKey}
                    />
                    {!!isShowFilter && aggregations?.map((filterItem: Aggregation) => renderFilter(filterItem))}
                </div>
                <div className="mt-4">
                    <Divider />
                </div>
                <Button onClick={() => setOpenFilter(false)} variant="secondary" className="m-4" loading={isLoading}>
                    Show results ({total} items)
                </Button>
            </DynamicDrawer>
            <Divider />

            <div className="block lg:hidden text-center">{!!breadcrumbs && <Breadcrumb items={breadcrumbs} />}</div>
        </Container>
    );
};

export default ProductList;
export const Control = ({ children, ...props }: ControlProps<any, false>) => (
    <components.Control {...props}>
        <b className="text-sm ml-2">Sort by: </b> {children}
    </components.Control>
);
